import { startOfDay } from 'date-fns'

import { LiveEventEvolutionEntry, LiveEventEvolutionTypeEnum } from '../../../live-events/types/LiveEventCollection'
import { LiveTrackingEvent, TrackingEvent } from '../../../live-events/types/TrackingEvents'

type EventInstance = {
  start: number
  end: number
  // Whether the event is a first appearance (a highlighted event's first instance). Not part
  // of the event getter's payload, so the caller supplies it.
  highlighted: boolean
  // The row's own first-instance flag, when the caller has a better source than the getter's durations.
  firstInstance?: boolean
}

export type InstanceAppearanceFlags = Pick<LiveEventEvolutionEntry, 'highlighted' | 'firstInstance'>

const toStartOfDayMs = (timestamp: number) => startOfDay(new Date(timestamp)).getTime()

/**
 * The first-appearance flags of the exact instance a row stands for, read off the blueprint's evolution
 * entries: the same source and the same instance-level rule the Blueprint tab uses for its NEW and
 * first-seen markers (`isNewEntry`). An entry is picked by exact start, then by calendar day, because
 * the rows and the evolution come from separate endpoints. `undefined` when no entry is that instance.
 */
export const instanceFlagsFromEvolution = (entries: LiveEventEvolutionEntry[] | undefined, start: number): InstanceAppearanceFlags | undefined => {
  const entry =
    entries?.find((candidate) => candidate.start === start) ?? entries?.find((candidate) => toStartOfDayMs(candidate.start) === toStartOfDayMs(start))
  return entry ? { highlighted: Boolean(entry.highlighted), firstInstance: Boolean(entry.firstInstance) } : undefined
}

/**
 * The tracked event the calendar hover popup (`LiveEventInfoTooltipContent`) reads, built from
 * the event getter (`GET /games/tracked/events/{id}`) instead of the full-history tracked-events search.
 *
 * The getter describes the event as a whole, so the instance the row stands for is picked out
 * of its durations by start day; `firstInstance` is only ever true on the earliest duration. Starts
 * are compared by calendar day because the blueprint rows and the getter are separate endpoints whose
 * timestamps can differ by a timezone offset (as in `useEventDialogDuration`). A caller that knows the
 * instance's own first-appearance flags (from the blueprint evolution) passes them in `instance`; they win
 * over the getter's durations so NEW and first-seen always come from one source.
 */
export const trackingEventFromEventGetter = ({ event, durations, motivations }: LiveTrackingEvent, instance: EventInstance): TrackingEvent => ({
  eventId: event.id,
  name: event.name ?? '',
  typeId: event.typeId ?? '',
  gameId: event.gameId ?? '',
  start: instance.start,
  end: instance.end,
  active: event.active,
  highlighted: instance.highlighted,
  mechanicChange: Boolean(event.blueprintEvolutionData?.types?.includes(LiveEventEvolutionTypeEnum.MechanicChange)),
  screenshotUrl: event.screenshotUrls?.[0],
  comment: event.comment,
  tags: event.tags,
  pricePoints: event.pricePoints,
  motivations,
  blueprintEvolutionData: event.blueprintEvolutionData,
  firstInstance:
    instance.firstInstance ?? Boolean(durations.find((duration) => toStartOfDayMs(duration.start) === toStartOfDayMs(instance.start))?.firstInstance),
  // Not shown by the popup and not on the event getter.
  recurring: false,
})
